import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnDestroy, ViewEncapsulation, Renderer2, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ResetPreferences, ResetOption, DEFAULT_RESET_PREFERENCES } from '../models/reset-preferences.model';
import { ResetPreferencesService } from '../services/reset-preferences.service';
import { ResetPreferencesConfigService } from '../services/reset-preferences-config.service';

@Component({
  selector: 'app-reset-preferences-drawer',
  templateUrl: './reset-preferences-drawer.component.html',
  styleUrls: ['./reset-preferences-drawer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms ease-out', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})
export class ResetPreferencesDrawerComponent implements OnInit, OnDestroy {
  @Input() moduleId: string = '';
  @Output() preferencesChanged = new EventEmitter<ResetPreferences>();
  @ViewChild('drawerElement') drawerElement?: ElementRef;

  isOpen = false;
  preferences: ResetPreferences = { ...DEFAULT_RESET_PREFERENCES };
  resetOptions: ResetOption[] = [];
  loading = false;
  private destroy$ = new Subject<void>();

  constructor(
    private resetPrefsService: ResetPreferencesService,
    private configService: ResetPreferencesConfigService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.moduleId) {
      this.resetOptions = this.configService.getResetOptions(this.moduleId);
      this.loadPreferences();
    }
    // Move wrapper to body to ensure it appears on top
    const wrapper = this.elementRef.nativeElement.querySelector('.reset-drawer-wrapper');
    if (wrapper && wrapper.parentElement !== document.body) {
      this.renderer.appendChild(document.body, wrapper);
    }
    // Subscribe to language changes to refresh translations
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  open(): void {
    if (this.moduleId) {
      this.resetOptions = this.configService.getResetOptions(this.moduleId);
      this.loadPreferences();
      this.isOpen = true;
      setTimeout(() => this.drawerElement?.nativeElement?.focus?.(), 100);
    }
  }

  close(): void {
    this.isOpen = false;
  }

  private loadPreferences(): void {
    this.preferences = this.resetPrefsService.getPreferences(this.moduleId);
  }

  toggleOption(optionKey: string): void {
    const key = optionKey as keyof ResetPreferences;
    if (key in this.preferences && typeof this.preferences[key] === 'boolean') {
      this.preferences[key] = !this.preferences[key] as any;
    }
  }

  savePreferences(): void {
    this.loading = true;
    setTimeout(() => {
      this.resetPrefsService.savePreferences(this.moduleId, this.preferences);
      this.preferencesChanged.emit(this.preferences);
      this.loading = false;
      this.close();
    }, 300);
  }

  resetAllToDefaults(): void {
    const msg = this.translate.instant('reset_prefs.confirm_reset');
    if (confirm(msg)) {
      this.preferences = { ...DEFAULT_RESET_PREFERENCES };
      this.savePreferences();
    }
  }

  getOptionLabel(optionKey: string): string {
    const option = this.resetOptions.find(o => o.key === optionKey);
    if (option?.labelKey) {
      const t = this.translate.instant(option.labelKey);
      // If translate returns the key (untranslated), fall back to English label
      if (t && t !== option.labelKey && !t.startsWith('?')) {
        return t;
      }
    }
    return option?.label || optionKey;
  }

  getOptionDescription(optionKey: string): string {
    const option = this.resetOptions.find(o => o.key === optionKey);
    if (option?.descKey) {
      const t = this.translate.instant(option.descKey);
      // If translate returns the key (untranslated), fall back to English description
      if (t && t !== option.descKey && !t.startsWith('?')) {
        return t;
      }
    }
    return option?.description || '';
  }

  isOptionChecked(optionKey: string): boolean {
    const key = optionKey as keyof ResetPreferences;
    if (key in this.preferences) {
      return (this.preferences[key] as any) === true;
    }
    return false;
  }

  onBackdropClick(): void {
    this.close();
  }

  closeOnEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }
}
